import React, { useState } from 'react';
import { connect } from 'react-redux';
import { IconButton, Typography } from '@mui/material';
import { Visibility, VisibilityOff, Add, Delete, Save } from '@mui/icons-material';
import { useConfirm } from 'material-ui-confirm';
import { removeAlertAC, createAlertAC, updateAlertAC } from '../../redux/actions/dataAlerts';
import { Page, Row, Fill } from '../util/Page';
import InlineTextEdit from '../util/InlineTextEdit';
import ListSelector from '../util/ListSelector';
import DurationSelector, { toUnit, toSeconds } from '../util/DurationSelector';
import BuildingMultiSelector from '../buildingselector/BuildingMultiSelector';
import ActionRow, { ActionCreator } from './ActionRow';
import AlertState from './AlertState';

/* eslint-disable jsx-a11y/label-has-associated-control */

const AlertEdit = ({ alert, onChange }) => (
  <>
    <div className="alert-col">
      <AlertState state={alert.state} />
      <InlineTextEdit
        label="Name"
        value={alert.name}
        onValueChange={(v) => onChange('name', v)}
        fullWidth={false}
      />
      <div className="site-container">
        <label>
          <span className="alert-label">Sites:</span>
          <BuildingMultiSelector
            selected={alert.buildings ?? []}
            onValueChange={(v) => onChange('buildings', v)}
          />
        </label>
      </div>
    </div>
    <div className="alert-col">
      <DurationSelector
        label="Data stale after"
        seconds={toSeconds(alert.minutes_stale, 'minutes')}
        onSecondsChange={(v) => onChange('minutes_stale', toUnit(v, 'minutes').toFixed(0))}
      />
    </div>
    <div className="alert-col">
      <AlertThresholdField alert={alert} onChange={onChange} />
      <ListSelector
        className="threshold-type-sel"
        label="Type"
        values={['stale points', 'percent stale']}
        selected={alert.point_threshold ? 'stale points' : 'percent stale'}
        onChange={(v) => {
          if (v === 'stale points') {
            onChange('point_threshold', alert.percent_threshold * 100.0, 'percent_threshold', null);
          } else {
            onChange(
              'point_threshold',
              null,
              'percent_threshold',
              Math.min(alert.point_threshold / 100.0, 1.0)
            );
          }
          return Promise.resolve('ok');
        }}
      />
    </div>
  </>
);

const AlertThresholdField = ({ alert, onChange }) =>
  alert.point_threshold != null ? (
    <InlineTextEdit
      type="number"
      label="Threshold"
      inputProps={{ min: 1 }}
      value={alert.point_threshold}
      onValueChange={(v) => onChange('point_threshold', v)}
      fullWidth={false}
    />
  ) : (
    <InlineTextEdit
      type="number"
      label="Threshold"
      /* max 100 ommitted to keep the form sizes the same, the min function will ensure value is valid */
      inputProps={{ min: 1 }}
      value={alert.percent_threshold * 100.0}
      onValueChange={(v) => onChange('percent_threshold', Math.min(v / 100.0, 1.0))}
      fullWidth={false}
    />
  );

const AlertCreator = connect(null, (dispatch) => ({
  create: (alert) => dispatch(createAlertAC(alert)),
}))(({ setCreating, create }) => {
  const [alert, setAlert] = useState({
    minutes_stale: 60,
    percent_threshold: null,
    point_threshold: 100,
    buildings: [],
    name: '',
  });
  return (
    <Page>
      <Row>
        <Fill />
        <Typography variant="h6">New Alert</Typography>
        <Fill />
      </Row>
      <Row>
        <AlertEdit
          alert={alert}
          onChange={(k1, v1, k2, v2) => {
            setAlert({ ...alert, [k1]: v1, [k2]: v2 });
            return Promise.resolve('ok');
          }}
        />
      </Row>
      <Row>
        <Fill />
        <IconButton onClick={() => setCreating(false)} size="large">
          Cancel
        </IconButton>
        <IconButton onClick={() => create(alert).then(() => setCreating(false))} size="large">
          Create alert <Save />
        </IconButton>
      </Row>
    </Page>
  );
});

export { AlertCreator };

const AlertRow = ({ alert, actions, update, del }) => {
  const [open, setOpen] = useState(false);
  const confirm = useConfirm();
  const [creatingAction, setCreatingAction] = useState(false);
  return (
    <Page name="alerts">
      <Row>
        <AlertEdit
          id={alert.id}
          alert={alert}
          onChange={(f1, v1, f2, v2) => update(alert.id, { ...alert, [f1]: v1, [f2]: v2 })}
        />
        <IconButton
          onClick={() =>
            confirm({
              title: 'Please confirm deletion',
              description: 'This will permanently delete your alert, and canot be undone',
              confirmationText: 'Delete alert',
            })
              .then(({ confirmed }) => (confirmed ? del(alert.id) : null))
              .catch(() => {})
          }
          size="large"
        >
          <Delete />
        </IconButton>
      </Row>
      <Row>
        {actions?.length > 0 ? (
          <IconButton onClick={() => setOpen(!open)} size="large">
            {!open ? 'View actions' : 'Hide actions'}
            {!open ? <Visibility /> : <VisibilityOff />}
          </IconButton>
        ) : null}
        <Fill />
        {!creatingAction ? (
          <IconButton
            onClick={() => {
              setCreatingAction(true);
              setOpen(true);
            }}
            size="large"
          >
            Create new action <Add />
          </IconButton>
        ) : null}
      </Row>
      {creatingAction ? <ActionCreator alertId={alert.id} setCreating={setCreatingAction} /> : null}
      {open
        ? actions.map((action) => <ActionRow key={action.id} alertId={alert.id} action={action} />)
        : null}
    </Page>
  );
};

export default connect(null, (dispatch) => ({
  update: (alertId, alert) => dispatch(updateAlertAC(alertId, alert)),
  del: (alertId) => dispatch(removeAlertAC(alertId)),
}))(AlertRow);
